// ============================================================================
// FSA ENGAGEMENT SETTINGS MODAL  (FSA-2.1)
// ============================================================================
// Create / Edit FSA settings for a PRIMA engagement. Currency + rounding
// default from the selected template if the user leaves them blank.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaEngagementService } from 'api/services/fsa/fsaEngagementService';
import { fsaTemplateService } from 'api/services/fsa/fsaTemplateService';
import { fsaRoundingFactorService } from 'api/services/fsa/fsaRoundingFactorService';
import { clientService } from 'api/services/clientService';
import { engagementTypeService } from 'api/services/engagementTypeService';
import type { ID } from 'api/types';
import type {
  FsaEngagementSettings,
  CreateFsaEngagementSettingsRequest,
  UpdateFsaEngagementSettingsRequest,
  FsaCarryForwardMode,
  FsaEligibleEngagement,
} from 'api/types/fsa/engagementSettings.types';
import type { FsEngagementCreationSpec } from 'api/types/fsa/rollForward.types';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /**
   * The engagement these settings apply to. `null` is allowed in create
   * mode only — the modal then renders an engagement picker at the top,
   * letting the user pick from engagements that don't yet have FSA
   * configured (loaded via GET /api/v1/FsaEngagements/eligible). When
   * non-null, the picker is hidden and the modal operates on that
   * engagement directly (the existing workspace flow).
   */
  engagementId: ID | null;
  existing: FsaEngagementSettings | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  /** Only used when the modal renders the picker (engagementId prop is null in create mode). */
  pickedEngagementId: string;
  /** Create-new-engagement path only (originate from FSA) — basic-tier context. */
  clientId: string;
  engagementTypeId: string;
  year: string; // text input — converted to number at save time
  templateId: string;
  financialYearEnd: string;
  periodStart: string;
  periodEnd: string;
  roundingFactorEnumId: string;
  roundingTolerance: string; // text input — converted to number at save time
  carryForwardMode: FsaCarryForwardMode;
  statusCode: string;
}

const INITIAL: FormData = {
  pickedEngagementId: '',
  clientId: '',
  engagementTypeId: '',
  year: String(new Date().getFullYear()),
  templateId: '',
  financialYearEnd: '',
  periodStart: '',
  periodEnd: '',
  roundingFactorEnumId: '',
  roundingTolerance: '1.00',
  carryForwardMode: 'CONTINUING',
  statusCode: 'ACTIVE',
};

// FSA-2.3 — `FIRST_YEAR_FROM_TB` was deprecated because the Opening
// column on a Tally / Zoho / SAP export carries the post-closing opening
// of the new FY (Balance Sheet only — P&L closed to Retained Earnings).
// Using it as a substitute for prior-year comparatives produces zero P&L
// comparatives, which is wrong. The two supported PY paths are now:
//   • CONTINUING — Roll Forward from a prior engagement (auto-seeds the
//     Prior Year TB from the prior engagement's consolidated closings)
//   • FIRST_YEAR_NIL — first-year / stand-alone engagement; PY comparatives
//     come from an explicit Prior Year TB upload via the General/Basic
//     tab CTA, or stay blank if no Prior Year TB is uploaded.
// The DB ENUM still accepts FIRST_YEAR_FROM_TB for back-compat with any
// existing rows; the dropdown just doesn't surface it for new settings.
const CF_MODES: { value: FsaCarryForwardMode; label: string }[] = [
  { value: 'CONTINUING',     label: 'Continuing engagement (carry prior year)' },
  { value: 'FIRST_YEAR_NIL', label: 'First year — upload Prior Year TB separately' },
];

const FsaEngagementSettingsModal: React.FC<Props> = ({
  isOpen, mode, engagementId, existing, onClose, onSaved,
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL);
  const initialSnapshot = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // Load templates for the dropdown. FSA-1 only ships seed templates so the
  // list is short — fetched once on open.
  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[]>([]);
  // FSA-2.2: rounding factor lookup (common.tbl_enum_master where enum_group = 'ROUNDING_FACTOR').
  const [roundingFactorOptions, setRoundingFactorOptions] = useState<{ value: string; label: string }[]>([]);
  // Eligible-engagement picker — populated only when the modal renders the
  // picker (create mode + null engagementId prop). Each label combines
  // client legal name + engagement code + type + year so it's recognisable.
  const [eligibleEngagementOptions, setEligibleEngagementOptions] = useState<{ value: string; label: string }[]>([]);
  // Engagement-type lookup for the "Create new engagement" path (small master — preloaded).
  const [engagementTypeOptions, setEngagementTypeOptions] = useState<{ value: string; label: string }[]>([]);

  // FSA can either ATTACH settings to an engagement that already exists in the
  // Engagements module ('existing' — the original picker flow) OR ORIGINATE a
  // brand-new engagement directly from FSA ('new' — Global Engagement Master,
  // ADR-0002, via POST FsaEngagements/create with no prior). The toggle is
  // offered only on the FSA-list "Add" flow (create mode + no engagement prop).
  const [createMode, setCreateMode] = useState<'existing' | 'new'>('new');

  const allowOriginate = mode === 'create' && !engagementId;
  const showEngagementPicker = allowOriginate && createMode === 'existing';
  const showCreateNew = allowOriginate && createMode === 'new';

  // Async client loader — survives large firm client rosters (scalable search).
  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName?.trim() ? `${c.name} (${c.legalName})` : c.name,
    }));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fsaTemplateService.getAll({ page: 1, pageSize: 100 }).then(r => {
      setTemplateOptions(r.data.map(t => ({
        value: t.id,
        label: `${t.code} · ${t.name}`,
      })));
    });
    fsaRoundingFactorService.getAll().then(r => {
      setRoundingFactorOptions((r.data ?? []).map(rf => ({
        value: String(rf.id),
        label: rf.enumValue,
      })));
    }).catch(() => { /* lookup is best-effort; user can leave blank */ });

    if (showEngagementPicker) {
      // pageSize 200 is generous — a firm should never realistically have
      // 200 in-flight unconfigured engagements at once. If that becomes a
      // problem the picker can move to async loadOptions with server-side
      // search; the endpoint already supports SearchTerm.
      fsaEngagementService.listEligible({ page: 1, pageSize: 200 }).then(r => {
        setEligibleEngagementOptions(r.data.map((e: FsaEligibleEngagement) => {
          const client = e.clientName?.trim() || '—';
          const type = e.engagementTypeName?.trim() || '—';
          return {
            value: String(e.id),
            label: `${client} · ${e.code} · ${type} · ${e.year}`,
          };
        }));
      }).catch(() => { /* surfaced via empty picker; user can retry by reopening */ });
    } else {
      setEligibleEngagementOptions([]);
    }

    // Engagement-type master for the "Create new engagement" path (small list).
    if (allowOriginate) {
      engagementTypeService.getAll({ page: 1, pageSize: 100 }).then(r => {
        setEngagementTypeOptions(r.data.map(t => ({ value: String(t.id), label: `${t.code} · ${t.name}` })));
      }).catch(() => { /* surfaced via empty picker; user can retry by reopening */ });
    }
  }, [isOpen, showEngagementPicker, allowOriginate]);

  useEffect(() => {
    if (!isOpen) return;
    setCreateMode('new'); // default the Add flow to direct origination
    const next: FormData = existing
      ? {
          ...INITIAL,
          templateId: String(existing.templateId),
          financialYearEnd: existing.financialYearEnd?.slice(0, 10) ?? '',
          periodStart: existing.periodStart?.slice(0, 10) ?? '',
          periodEnd: existing.periodEnd?.slice(0, 10) ?? '',
          roundingFactorEnumId: existing.roundingFactorEnumId ? String(existing.roundingFactorEnumId) : '',
          roundingTolerance: existing.roundingTolerance != null ? String(existing.roundingTolerance) : '1.00',
          carryForwardMode: existing.carryForwardMode ?? 'CONTINUING',
          statusCode: existing.statusCode ?? 'ACTIVE',
        }
      : { ...INITIAL };
    setFormData(next);
    initialSnapshot.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen, existing]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot.current) return false;
    return JSON.stringify(formData) !== initialSnapshot.current;
  }, [formData]);

  const setField = useCallback(<K extends keyof FormData>(k: K, v: FormData[K]) => {
    setFormData(prev => ({ ...prev, [k]: v }));
    setFieldErrors(prev => {
      if (!prev[k as string]) return prev;
      const next = { ...prev };
      delete next[k as string];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (showEngagementPicker && !formData.pickedEngagementId) {
      errors.pickedEngagementId = 'Engagement is required';
    }
    if (showCreateNew) {
      // Originate path — basic-tier context is required (FsEngagementCreationSpec).
      if (!formData.clientId) errors.clientId = 'Client is required';
      if (!formData.engagementTypeId) errors.engagementTypeId = 'Engagement type is required';
      const yr = Number(formData.year);
      if (!formData.year || !Number.isInteger(yr) || yr < 2000 || yr > 2100) {
        errors.year = 'Enter a valid year (2000–2100)';
      }
      // Period is optional for attach, but the create endpoint requires it.
      if (!formData.periodStart) errors.periodStart = 'Period start is required';
      if (!formData.periodEnd) errors.periodEnd = 'Period end is required';
    }
    if (!formData.templateId) errors.templateId = 'Template is required';
    if (!formData.financialYearEnd) errors.financialYearEnd = 'Financial year end is required';
    if (formData.periodStart && formData.periodEnd && formData.periodEnd < formData.periodStart) {
      errors.periodEnd = 'Period end must be on or after period start';
    }
    // Tolerance must be a non-negative finite number.
    if (formData.roundingTolerance) {
      const t = Number(formData.roundingTolerance);
      if (!Number.isFinite(t) || t < 0) {
        errors.roundingTolerance = 'Tolerance must be a non-negative number';
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSaveConfirmOpen(true);
  }, [formData, showEngagementPicker, showCreateNew]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const toleranceNum = formData.roundingTolerance ? Number(formData.roundingTolerance) : null;

      // ORIGINATE path — create a brand-new engagement directly from FSA
      // (Global Engagement Master, ADR-0002). One call creates the basic-tier
      // engagement AND its FSA settings; no PriorEngagementId → first-year.
      if (showCreateNew) {
        const spec: FsEngagementCreationSpec = {
          clientId: formData.clientId,
          engagementTypeId: formData.engagementTypeId,
          year: Number(formData.year),
          periodStart: formData.periodStart,
          periodEnd: formData.periodEnd,
          financialYearEnd: formData.financialYearEnd,
          templateId: formData.templateId,
          roundingFactorEnumId: formData.roundingFactorEnumId || null,
        };
        const resp = await fsaEngagementService.createEngagement(spec);
        setSaving(false);
        notificationService.toast.success(resp?.data?.message ?? 'Engagement created successfully.');
        onSaved();
        return;
      }

      // Resolve target engagement: prop wins when provided (workspace flow);
      // otherwise use the picker value (FSA-list "Add" flow).
      const targetEngagementId: ID | '' = engagementId ?? formData.pickedEngagementId;
      if (mode === 'create') {
        if (!targetEngagementId) {
          // Validation already guards this; defensive check just in case.
          setSaving(false);
          notificationService.toast.error('Engagement is required.');
          return;
        }
        const request: CreateFsaEngagementSettingsRequest = {
          templateId: formData.templateId,
          financialYearEnd: formData.financialYearEnd,
          periodStart: formData.periodStart || null,
          periodEnd: formData.periodEnd || null,
          roundingFactorEnumId: formData.roundingFactorEnumId || null,
          roundingTolerance: toleranceNum,
          carryForwardMode: formData.carryForwardMode,
          statusCode: formData.statusCode,
        };
        await fsaEngagementService.createSettings(targetEngagementId, request);
      } else if (existing && targetEngagementId) {
        const request: UpdateFsaEngagementSettingsRequest = {
          templateId: formData.templateId,
          financialYearEnd: formData.financialYearEnd,
          periodStart: formData.periodStart || null,
          periodEnd: formData.periodEnd || null,
          roundingFactorEnumId: formData.roundingFactorEnumId || null,
          roundingTolerance: toleranceNum,
          carryForwardMode: formData.carryForwardMode,
          statusCode: formData.statusCode,
          rowVersion: existing.rowVersion,
        };
        await fsaEngagementService.updateSettings(targetEngagementId, existing.id, request);
      }
      setSaving(false);
      onSaved();
    } catch (err: any) {
      setSaving(false);
      // Match the Roll Forward flow's error extraction — surface server
      // validation arrays (e.g. the duplicate-FSA-engagement guard), not just
      // a single message.
      const data = err?.response?.data;
      const msg = data?.message
        ?? (Array.isArray(data?.errors) && data.errors.length ? data.errors.join('; ') : null)
        ?? err?.message
        ?? (showCreateNew ? 'Failed to create engagement.' : 'Failed to save FSA settings.');
      notificationService.toast.error(msg);
    }
  }, [mode, engagementId, existing, formData, onSaved, showCreateNew]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-md">
        {saving && <LoadingOverlay />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? (showCreateNew ? 'New FSA Engagement' : 'Configure FSA') : 'Edit FSA Settings'}
          </h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {allowOriginate && (
            <div className="form-group">
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className={`btn ${createMode === 'new' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1 }}
                  onClick={() => setCreateMode('new')}
                  disabled={saving}
                >
                  Create new engagement
                </button>
                <button
                  type="button"
                  className={`btn ${createMode === 'existing' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1 }}
                  onClick={() => setCreateMode('existing')}
                  disabled={saving}
                >
                  Attach to existing
                </button>
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                {createMode === 'new'
                  ? 'Originate a brand-new engagement directly from FSA (client + type + year).'
                  : 'Attach FSA to an engagement already created in the Engagements module.'}
              </div>
            </div>
          )}

          {showCreateNew && (
            <>
              <div className="form-group" data-field="clientId">
                <SyutSelect
                  label={<>Client <span className="text-danger">*</span></>}
                  value={formData.clientId}
                  onChange={(v) => setField('clientId', (v as string) || '')}
                  isAsync
                  loadOptions={loadClientOptions}
                  defaultOptions
                  cacheOptions={false}
                  error={fieldErrors.clientId}
                  placeholder="Search client…"
                />
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group" data-field="engagementTypeId">
                  <SyutSelect
                    label={<>Engagement Type <span className="text-danger">*</span></>}
                    value={formData.engagementTypeId}
                    onChange={(v) => setField('engagementTypeId', (v as string) || '')}
                    options={engagementTypeOptions}
                    isSearchable
                    error={fieldErrors.engagementTypeId}
                  />
                </div>
                <div className="form-group" data-field="year">
                  <label className="form-label">Year <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-input${fieldErrors.year ? ' form-field-error' : ''}`}
                    value={formData.year}
                    onChange={(e) => setField('year', e.target.value)}
                    placeholder="2026"
                  />
                  {fieldErrors.year && <div className="form-error-message">{fieldErrors.year}</div>}
                </div>
              </div>
            </>
          )}

          {showEngagementPicker && (
            <div className="form-group" data-field="pickedEngagementId">
              <SyutSelect
                label={<>Engagement <span className="text-danger">*</span></>}
                value={formData.pickedEngagementId}
                onChange={(v) => setField('pickedEngagementId', (v as string) || '')}
                options={eligibleEngagementOptions}
                isSearchable
                error={fieldErrors.pickedEngagementId}
                placeholder={
                  eligibleEngagementOptions.length === 0
                    ? 'No engagements available (all are already configured)'
                    : 'Pick an engagement…'
                }
                helperText="Lists engagements that don't yet have FSA configured."
              />
            </div>
          )}

          <div className="form-group" data-field="templateId">
            <SyutSelect
              label={<>Template <span className="text-danger">*</span></>}
              value={formData.templateId}
              onChange={(v) => setField('templateId', (v as string) || '')}
              options={templateOptions}
              isSearchable
              error={fieldErrors.templateId}
            />
          </div>

          <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group" data-field="financialYearEnd">
              <label className="form-label">
                Financial Year End <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`form-input${fieldErrors.financialYearEnd ? ' form-field-error' : ''}`}
                value={formData.financialYearEnd}
                onChange={(e) => setField('financialYearEnd', e.target.value)}
              />
              {fieldErrors.financialYearEnd && (
                <div className="form-error-message">{fieldErrors.financialYearEnd}</div>
              )}
            </div>

            <div className="form-group">
              {/* A brand-new engagement has no prior to carry from — it is
                  always first-year (the create endpoint forces it). The field
                  stays a disabled dropdown for visual consistency with attach
                  mode; PY comparatives come later from a Prior Year TB upload. */}
              <SyutSelect
                label="Carry-forward Mode"
                value={showCreateNew ? 'FIRST_YEAR_NIL' : formData.carryForwardMode}
                onChange={(v) => setField('carryForwardMode', (v as FsaCarryForwardMode) || 'CONTINUING')}
                options={CF_MODES.map(m => ({ value: m.value, label: m.label }))}
                isClearable={false}
                isDisabled={showCreateNew}
              />
            </div>
          </div>

          <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group" data-field="periodStart">
              <label className="form-label">
                Period Start {showCreateNew ? <span className="text-danger">*</span> : '(optional)'}
              </label>
              <input
                type="date"
                className={`form-input${fieldErrors.periodStart ? ' form-field-error' : ''}`}
                value={formData.periodStart}
                onChange={(e) => setField('periodStart', e.target.value)}
              />
              {fieldErrors.periodStart && (
                <div className="form-error-message">{fieldErrors.periodStart}</div>
              )}
            </div>
            <div className="form-group" data-field="periodEnd">
              <label className="form-label">
                Period End {showCreateNew ? <span className="text-danger">*</span> : '(optional)'}
              </label>
              <input
                type="date"
                className={`form-input${fieldErrors.periodEnd ? ' form-field-error' : ''}`}
                value={formData.periodEnd}
                onChange={(e) => setField('periodEnd', e.target.value)}
              />
              {fieldErrors.periodEnd && (
                <div className="form-error-message">{fieldErrors.periodEnd}</div>
              )}
            </div>
          </div>

          {/* FSA-2.2: rounding factor + tolerance — drive the Notes screen's
              rounded-value computation. Factor is optional (NULL = inherit
              template default); tolerance applies note-rounding overrides. */}
          <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: 4 }}>
            <div className="form-group">
              <SyutSelect
                label="Rounding Factor"
                value={formData.roundingFactorEnumId}
                onChange={(v) => setField('roundingFactorEnumId', (v as string) || '')}
                options={roundingFactorOptions}
                isClearable
                placeholder="(use template default)"
                helperText="NULL inherits from the engagement's template."
              />
            </div>
            <div className="form-group" data-field="roundingTolerance">
              <label className="form-label">Rounding Tolerance</label>
              <input
                type="number"
                step="0.01"
                min={0}
                className={`form-input${fieldErrors.roundingTolerance ? ' form-field-error' : ''}`}
                value={formData.roundingTolerance}
                onChange={(e) => setField('roundingTolerance', e.target.value)}
                placeholder="1.00"
              />
              {fieldErrors.roundingTolerance ? (
                <div className="form-error-message">{fieldErrors.roundingTolerance}</div>
              ) : (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  Max drift (in rounded units) before a note-rounding override is auto-replaced on save.
                </div>
              )}
            </div>
          </div>

          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 8 }}>
            Currency defaults from the selected template. Override becomes available later.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={
          showCreateNew
            ? 'This will create a new engagement and configure FSA on it. Continue?'
            : `Are you sure you want to ${mode === 'create' ? 'create' : 'update'} the FSA settings for this engagement?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: 12,
  }}>
    <div style={{
      width: 32, height: 32, border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Saving…</span>
  </div>
);

export default FsaEngagementSettingsModal;
